import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications, getNotificationsCount,
  markNotificationRead, markAllNotificationsRead
} from '../../services/api';
import './NotificationBell.css';

const POLL_MS = 30000;

/* «5 мин назад» читается быстрее, чем дата с временем: в ленте важно,
   свежее событие или вчерашнее. Дальше суток — обычная дата. */
const timeAgo = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return 'только что';
  if (mins < 60) return `${mins} мин назад`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)} ч назад`;
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const data = await getNotificationsCount();
      setUnread(data.unread);
    } catch {
      /* сеть моргнула — счётчик подтянется следующим опросом */
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNotifications({ limit: 30 });
      setItems(data.notifications);
      setUnread(data.unread);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Счётчик опрашиваем по таймеру, список — только когда открыли колокольчик
  useEffect(() => {
    loadCount();
    const timer = setInterval(loadCount, POLL_MS);
    return () => clearInterval(timer);
  }, [loadCount]);

  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  // Клик мимо и Escape закрывают панель
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const openItem = async (item) => {
    setOpen(false);
    if (!item.is_read) {
      setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)));
      setUnread((n) => Math.max(0, n - 1));
      try {
        await markNotificationRead(item.id);
      } catch {
        loadCount();
      }
    }
    if (item.link) navigate(item.link);
  };

  const readAll = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnread(0);
    try {
      await markAllNotificationsRead();
    } catch {
      loadCount();
    }
  };

  return (
    <div className="bell" ref={rootRef}>
      <button
        className={`bell-btn ${open ? 'open' : ''}`}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={unread > 0 ? `Уведомления, непрочитанных: ${unread}` : 'Уведомления'}
        title="Уведомления"
      >
        <svg
          className="bell-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18.7491 9.70957V9.00496C18.7491 5.13623 15.7274 2 12 2C8.27256 2 5.25087 5.13623 5.25087 9.00496V9.70957C5.25087 10.5552 5.00972 11.3818 4.5578 12.0854L3.45036 13.8095C2.43882 15.3843 3.21105 17.5249 4.97036 18.0229C9.57274 19.3257 14.4273 19.3257 19.0296 18.0229C20.789 17.5249 21.5612 15.3843 20.5496 13.8095L19.4422 12.0854C18.9903 11.3818 18.7491 10.5552 18.7491 9.70957Z" />
          <path d="M7.5 19C8.15503 20.7478 9.92246 22 12 22C14.0775 22 15.845 20.7478 16.5 19" />
        </svg>
        {unread > 0 && <span className="bell-count">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="bell-panel">
          <div className="bell-panel-head">
            <span>Уведомления</span>
            {unread > 0 && (
              <button className="bell-readall" type="button" onClick={readAll}>
                Прочитать все
              </button>
            )}
          </div>

          <div className="bell-list">
            {loading && <div className="bell-empty">Загрузка…</div>}

            {!loading && items.length === 0 && (
              <div className="bell-empty">Пока ничего нового</div>
            )}

            {!loading && items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`bell-item ${item.is_read ? '' : 'unread'}`}
                onClick={() => openItem(item)}
              >
                <span className="bell-item-title">{item.title}</span>
                {item.body && <span className="bell-item-body">{item.body}</span>}
                <span className="bell-item-time">{timeAgo(item.created_at)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
